import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import { Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { readingsApi, type Meter, type MeterCreate, type MeterUpdate } from '../api/readings'
import { geoApi, type GeoResult } from '../api/geo'
import { useAuthStore } from '../store/authStore'
import { Badge } from '../components/Badge'
import { LeafletMap, FALLBACK_CENTER } from '../components/Map'
import { Drawer } from '../components/Drawer'
import { DockedMapEditor } from '../components/map-edit/DockedMapEditor'

// Backend geocoding proxy (meters-to-web-mobile-2026-09-27-geo-and-bbox-
// contract.md) -- replaces the old client-side Google Geocoding call, so
// the Google Maps key no longer ships in the web bundle at all.
async function geocodeAddress(address: string) {
  const { data } = await geoApi.geocode(address)
  return { lat: data.lat.toFixed(6), lng: data.lng.toFixed(6), precision: data.precision, source: data.source }
}

// ── Map-click placement — single point, same DockedMapEditor ScadaView uses ───
// for zone boundaries/pipeline paths. A meter is just the one-point case: no
// renderOverlay (no line/ring to draw), no onDeletePoint (clearing GPS stays
// on the typed fields below, not the map picker's job).

function MeterMapPickerModal({ lat, lng, onPick, onClose }: {
  lat: string; lng: string
  onPick: (lat: string, lng: string) => void
  onClose: () => void
}) {
  const initial = (() => {
    const la = parseFloat(lat), lo = parseFloat(lng)
    return Number.isFinite(la) && Number.isFinite(lo) ? { lat: la, lng: lo } : null
  })()
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(initial)

  const mapCenter: [number, number] = point ? [point.lat, point.lng] : FALLBACK_CENTER

  return (
    <div className="fixed inset-2 sm:inset-4 z-[1100] bg-white rounded-overlay shadow-overlay flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
        <h3 className="font-semibold text-gray-900">Postavi lokaciju na mapi</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
      </div>

      <div className="flex-1 min-h-0">
        <DockedMapEditor
          points={point ? [point] : []}
          mapCenter={mapCenter}
          markerIcon={() => makeMeterIcon(true)}
          panelTitle={() => 'Lokacija vodomera'}
          onCommitPoint={(_i, la, lo) => setPoint({ lat: la, lng: lo })}
          onAddPoint={(la, lo) => { setPoint({ lat: la, lng: lo }); return 0 }}
        />
      </div>

      <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 shrink-0">
        <button onClick={onClose} className="text-sm font-medium text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg border border-gray-300">
          Otkaži
        </button>
        <button
          onClick={() => { if (point) { onPick(point.lat.toFixed(6), point.lng.toFixed(6)); onClose() } }}
          disabled={!point}
          className="text-sm font-medium text-brand-midnight bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-40 px-4 py-2 rounded-lg"
        >
          Primeni
        </button>
      </div>
    </div>
  )
}

// ── Reusable GPS coordinate fields ────────────────────────────────────────────

function GpsFields({
  lat, lng, geocodeFrom, onLatChange, onLngChange,
}: {
  lat: string; lng: string
  geocodeFrom: string
  onLatChange: (v: string) => void
  onLngChange: (v: string) => void
}) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [geocodeInfo, setGeocodeInfo] = useState<{ precision: GeoResult['precision']; source: GeoResult['source'] } | null>(null)
  const [errorText, setErrorText] = useState('')

  const fromAddress = async () => {
    if (!geocodeFrom.trim()) return
    setStatus('loading')
    setGeocodeInfo(null)
    try {
      const { lat: la, lng: lo, precision, source } = await geocodeAddress(geocodeFrom)
      onLatChange(la); onLngChange(lo)
      setGeocodeInfo({ precision, source })
      setStatus('ok')
    } catch (e: unknown) {
      const status404 = (e as { response?: { status?: number } })?.response?.status === 404
      setErrorText(status404 ? 'Adresa nije pronađena.' : 'Nije moguće preuzeti GPS koordinate.')
      setStatus('error')
    }
  }

  const fromBrowser = () => {
    if (!navigator.geolocation) { setStatus('error'); return }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onLatChange(pos.coords.latitude.toFixed(6))
        onLngChange(pos.coords.longitude.toFixed(6))
        setGeocodeInfo(null)
        setStatus('ok')
      },
      () => setStatus('error'),
    )
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-2">GPS lokacija</label>
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          onClick={fromAddress}
          disabled={status === 'loading' || !geocodeFrom.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-brand-cyan-text rounded-lg border border-blue-200 disabled:opacity-50 transition-colors"
        >
          {status === 'loading' ? (
            <span className="w-3 h-3 border-2 border-brand-cyan border-t-transparent rounded-full animate-spin inline-block" />
          ) : (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          )}
          Iz adrese
        </button>
        <button
          type="button"
          onClick={fromBrowser}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-200 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Moja lokacija
        </button>
        <button
          type="button"
          onClick={() => setShowMapPicker(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-200 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Na mapi
        </button>
      </div>
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Latitude</label>
          <input
            type="text"
            value={lat}
            onChange={(e) => onLatChange(e.target.value)}
            placeholder="44.265000"
            className="w-full text-xs font-mono border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden bg-gray-50"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Longitude</label>
          <input
            type="text"
            value={lng}
            onChange={(e) => onLngChange(e.target.value)}
            placeholder="20.085000"
            className="w-full text-xs font-mono border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden bg-gray-50"
          />
        </div>
      </div>
      {status === 'ok' && (
        <div className="mt-1 space-y-0.5">
          <p className="text-xs text-emerald-600">
            ✓ Koordinate preuzete
            {geocodeInfo && (geocodeInfo.precision === 'street' || geocodeInfo.precision === 'settlement') && (
              <> — približno ({geocodeInfo.precision === 'street' ? 'centar ulice' : 'centar naselja'}, ne tačna adresa)</>
            )}
          </p>
          {geocodeInfo?.source === 'nominatim' && (
            <p className="text-[10px] text-gray-400">© OpenStreetMap contributors</p>
          )}
        </div>
      )}
      {status === 'error' && <p className="text-xs text-red-600 mt-1">{errorText}</p>}

      {showMapPicker && (
        <MeterMapPickerModal
          lat={lat} lng={lng}
          onPick={(la, lo) => { onLatChange(la); onLngChange(lo); setGeocodeInfo(null); setStatus('ok') }}
          onClose={() => setShowMapPicker(false)}
        />
      )}
    </div>
  )
}

function makeMeterIcon(active: boolean) {
  const color = active ? '#10b981' : '#9ca3af'
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  })
}

// ── Meter map view ────────────────────────────────────────────────────────────

function boundsToBbox(b: L.LatLngBounds): string {
  return `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`
}

// Fetches by viewport (meters-to-web-mobile-2026-09-27-geo-and-bbox-
// contract.md) instead of the table view's limit/skip paging -- a tenant
// with >500 meters used to have every meter past the 500th silently
// invisible on this map. Lives inside <LeafletMap> to read its bounds via
// useMap/useMapEvents; the initial bbox comes from the map's own bounds at
// mount (available synchronously once it has a size), later ones from
// moveend, debounced so a drag/zoom doesn't fire a request per frame.
function MeterMapMarkers({ search, showInactive }: { search: string; showInactive: boolean }) {
  const map = useMap()
  const [bbox, setBbox] = useState(() => boundsToBbox(map.getBounds()))
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  useMapEvents({
    moveend: () => {
      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => setBbox(boundsToBbox(map.getBounds())), 400)
    },
  })

  const { data: meters = [] } = useQuery({
    queryKey: ['meters-bbox', bbox, search, showInactive],
    queryFn: () => readingsApi.getMeters({
      bbox,
      search: search || undefined,
      active: showInactive ? undefined : true,
    }).then((r) => r.data),
    staleTime: 30_000,
  })
  // A meter with no coordinates has a NULL location and never matches a
  // bbox server-side, but the response type is still Meter (nullable
  // lat/lng) -- this narrows for the position prop below.
  const mapped = meters.filter((m): m is Meter & { latitude: number; longitude: number } =>
    m.latitude != null && m.longitude != null)

  return (
    <>
      {mapped.map((m) => (
        <Marker key={m.id} position={[m.latitude, m.longitude]} icon={makeMeterIcon(m.active)}>
          <Popup>
            <div className="text-sm">
              <p className="font-semibold text-gray-900">{m.subscriber_name}</p>
              <p className="text-gray-500 font-mono text-xs">{m.meter_serial}</p>
              <p className="text-gray-600 mt-1">{m.address}</p>
              {m.last_reading_value != null && (
                <p className="mt-1 text-brand-cyan-text font-medium">
                  {m.last_reading_value.toFixed(3)} m³
                </p>
              )}
              <p className="mt-1">{m.active ? '✅ Aktivan' : '⬜ Neaktivan'}</p>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  )
}

function MeterMapView({ search, showInactive }: { search: string; showInactive: boolean }) {
  return (
    <LeafletMap center={FALLBACK_CENTER} zoom={14}>
      <MeterMapMarkers search={search} showInactive={showInactive} />
    </LeafletMap>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('sr-Latn-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function useDebounce(delay = 350) {
  const [deb, setDeb] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const set = (v: string) => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setDeb(v), delay)
  }
  return [deb, set] as const
}

const CAN_EDIT_ROLES    = ['admin', 'company_admin', 'dispatcher']
const CAN_READ_ROLES    = ['admin', 'company_admin', 'meter_reader']

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <Badge tone="success" dot dotClassName="bg-emerald-500">Aktivan</Badge>
  ) : (
    <Badge className="bg-gray-100 text-gray-500" dot dotClassName="bg-gray-400">Neaktivan</Badge>
  )
}

// ── Add reading form ──────────────────────────────────────────────────────────

function AddReadingForm({ meterId, onDone }: { meterId: string; onDone: () => void }) {
  const qc = useQueryClient()
  const today = new Date().toISOString().slice(0, 16) // datetime-local format
  const [value, setValue] = useState('')
  const [date, setDate]   = useState(today)
  const [notes, setNotes] = useState('')
  const [err, setErr]     = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      readingsApi.createReading({
        meter_id:      meterId,
        reading_value: parseFloat(value),
        reading_date:  new Date(date).toISOString(),
        notes:         notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['readings', meterId] })
      qc.invalidateQueries({ queryKey: ['meters'] })
      onDone()
    },
    onError: () => setErr('Greška. Proverite vrednost.'),
  })

  return (
    <div className="border border-blue-200 bg-blue-50 rounded-xl p-4 space-y-3">
      <p className="text-xs font-semibold text-brand-cyan-text uppercase tracking-wide">Novo očitavanje</p>

      <div>
        <label className="block text-xs text-gray-600 mb-1">Vrednost (m³) <span className="text-red-500">*</span></label>
        <input
          type="number"
          step="0.001"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="npr. 123.456"
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1">Datum i vreme</label>
        <input
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1">Napomena</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Opciono…"
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
        />
      </div>

      {err && <p className="text-xs text-red-600">{err}</p>}

      <div className="flex gap-2">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !value}
          className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-xs font-semibold py-2 rounded-lg disabled:opacity-50"
        >
          {mutation.isPending ? 'Čuvanje…' : 'Sačuvati'}
        </button>
        <button
          onClick={onDone}
          className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold py-2 rounded-lg"
        >
          Otkazati
        </button>
      </div>
    </div>
  )
}

// ── Meter drawer ──────────────────────────────────────────────────────────────

function MeterDrawer({
  meter,
  onClose,
  canEdit,
  canRead,
}: {
  meter: Meter
  onClose: () => void
  canEdit: boolean
  canRead: boolean
}) {
  const qc = useQueryClient()
  const [tab, setTab]         = useState<'details' | 'history'>('details')
  const [editing, setEditing] = useState(false)
  const [addingReading, setAddingReading] = useState(false)
  const [form, setForm] = useState<MeterUpdate>({
    subscriber_name: meter.subscriber_name,
    address: meter.address,
    meter_type: meter.meter_type ?? '',
    notes: meter.notes ?? '',
    active: meter.active,
  })
  const [lat, setLat] = useState(meter.latitude?.toFixed(6)  ?? '')
  const [lng, setLng] = useState(meter.longitude?.toFixed(6) ?? '')

  // Readings history
  const { data: readings = [], isLoading: readingsLoading } = useQuery({
    queryKey: ['readings', meter.id],
    queryFn: () => readingsApi.list({ meter_id: meter.id, limit: 24 }).then((r) => r.data),
    enabled: tab === 'history',
    staleTime: 60_000,
  })

  // Chart data (chronological)
  const chartData = [...readings]
    .reverse()
    .map((r) => ({
      date: new Date(r.reading_date).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit' }),
      value: r.reading_value,
    }))

  const updateMutation = useMutation({
    mutationFn: (body: MeterUpdate) => readingsApi.updateMeter(meter.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meters'] })
      setEditing(false)
    },
  })

  const toggleActive = useMutation({
    mutationFn: () => readingsApi.updateMeter(meter.id, { active: !meter.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meters'] }),
  })

  return (
    <Drawer
      onClose={onClose}
      title={
        <>
          <h2 className="text-base font-bold text-gray-900 leading-tight">{meter.subscriber_name}</h2>
          <p className="text-xs text-gray-400 mt-0.5 font-mono">{meter.meter_serial}</p>
          {meter.reading_route_name ? (
            <Badge className="mt-1 bg-blue-100 text-brand-cyan-text">{meter.reading_route_name}</Badge>
          ) : (
            <Badge className="mt-1 bg-gray-100 text-gray-400">Nije u turi</Badge>
          )}
        </>
      }
    >
      {/* Tabs */}
      <div className="flex border-b border-gray-100 shrink-0">
        {(['details', 'history'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              tab === t
                ? 'border-brand-cyan text-brand-cyan-text'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t === 'details' ? 'Detalji' : 'Istorija'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'details' ? (
          <div className="px-6 py-5 space-y-5">
            {/* Status */}
            <div className="flex items-center justify-between">
              <StatusBadge active={meter.active} />
              {canEdit && (
                <button
                  onClick={() => toggleActive.mutate()}
                  disabled={toggleActive.isPending}
                  className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2 disabled:opacity-50"
                >
                  {meter.active ? 'Deaktivirati' : 'Aktivirati'}
                </button>
              )}
            </div>

            {/* Detail fields / edit form */}
            {editing ? (
              <div className="space-y-4">
                {([
                  { key: 'subscriber_name' as const, label: 'Pretplatnik' },
                  { key: 'address' as const,         label: 'Adresa' },
                ] as const).map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                    <input
                      type="text"
                      value={form[key] ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Tip vodomera</label>
                  <select
                    value={form.meter_type ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, meter_type: e.target.value }))}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden bg-white"
                  >
                    <option value="">— nije odabrano —</option>
                    <option value="cold">Hladna voda</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Napomena</label>
                  <textarea
                    rows={3}
                    value={form.notes ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden resize-none"
                  />
                </div>
                <GpsFields
                  lat={lat} lng={lng}
                  geocodeFrom={form.address ?? ''}
                  onLatChange={setLat}
                  onLngChange={setLng}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => updateMutation.mutate({
                      ...form,
                      // null, not undefined -- PUT now reads an omitted key
                      // as "leave alone," so a cleared GPS fix has to
                      // actually say so to clear rather than sticking.
                      latitude:  lat ? parseFloat(lat)  : null,
                      longitude: lng ? parseFloat(lng)  : null,
                    })}
                    disabled={updateMutation.isPending}
                    className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2 rounded-lg disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Čuvanje…' : 'Sačuvati'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 rounded-lg"
                  >
                    Odustati
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  ['Adresa',            meter.address],
                  ['Tip vodomera',      meter.meter_type],
                  ['Datum instalacije', fmtDate(meter.installation_date)],
                  ['Dodat u sistem',    fmtDate(meter.created_at)],
                  ['GPS',               meter.latitude ? `${meter.latitude.toFixed(5)}, ${meter.longitude?.toFixed(5)}` : null],
                  ['Napomena',          meter.notes],
                ].map(([label, val]) => (
                  <div key={String(label)}>
                    <p className="text-xs font-medium text-gray-400">{label}</p>
                    <p className="text-sm text-gray-800 mt-0.5">{val || '—'}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Last reading summary */}
            <div className="bg-linear-to-br from-brand-midnight to-brand-midnight-lighter rounded-xl p-4 text-white">
              <p className="text-xs font-semibold text-blue-200 mb-2">Poslednje očitavanje</p>
              {meter.last_reading_value !== null ? (
                <div className="flex items-end justify-between">
                  <p className="text-3xl font-bold">
                    {meter.last_reading_value.toFixed(3)}
                    <span className="text-base font-normal ml-1 text-blue-200">m³</span>
                  </p>
                  <p className="text-xs text-blue-300">{fmtDate(meter.last_reading_date)}</p>
                </div>
              ) : (
                <p className="text-sm text-blue-200 italic">Nema očitavanja</p>
              )}
            </div>
          </div>
        ) : (
          /* ── History tab ── */
          <div className="px-6 py-5 space-y-5">
            {/* Add reading */}
            {canRead && !addingReading && (
              <button
                onClick={() => setAddingReading(true)}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-brand-cyan-text rounded-xl py-3 text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Dodati očitavanje
              </button>
            )}

            {addingReading && (
              <AddReadingForm
                meterId={meter.id}
                onDone={() => setAddingReading(false)}
              />
            )}

            {readingsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
              </div>
            ) : readings.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">Nema istorije očitavanja</p>
              </div>
            ) : (
              <>
                {/* Chart */}
                {chartData.length > 1 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Trend (poslednjih {chartData.length} očitavanja)
                    </p>
                    <ResponsiveContainer width="100%" height={160}>
                      <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="readingGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#37C8FC" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#37C8FC" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                        <Tooltip
                          formatter={(v: number) => [`${v.toFixed(3)} m³`, 'Stanje']}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="value"
                          stroke="#37C8FC"
                          strokeWidth={2}
                          fill="url(#readingGrad)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Readings table */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Poslednja očitavanja
                  </p>
                  <div className="space-y-1">
                    {readings.slice(0, 12).map((r, i) => (
                      <div
                        key={r.id}
                        className={`flex items-center justify-between py-2.5 px-3 rounded-lg ${i === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {r.reading_value.toFixed(3)} m³
                          </p>
                          {r.notes && <p className="text-xs text-gray-400 mt-0.5">{r.notes}</p>}
                        </div>
                        <p className="text-xs text-gray-500">{fmtDateTime(r.reading_date)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {tab === 'details' && canEdit && !editing && (
        <div className="px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium py-2.5 rounded-lg"
          >
            Izmeniti podatke
          </button>
        </div>
      )}
    </Drawer>
  )
}

// ── Create meter modal ────────────────────────────────────────────────────────

function CreateMeterModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<MeterCreate>({
    subscriber_name: '',
    address: '',
    meter_serial: '',
    meter_type: '',
  })
  const [lat, setLat]   = useState('')
  const [lng, setLng]   = useState('')
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (body: MeterCreate) => readingsApi.createMeter(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meters'] })
      onClose()
    },
    onError: () => setError('Greška pri kreiranju. Proverite serijski broj.'),
  })

  const set = (key: keyof MeterCreate) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSubmit = () => {
    setError('')
    mutation.mutate({
      ...form,
      latitude:  lat ? parseFloat(lat)  : undefined,
      longitude: lng ? parseFloat(lng)  : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-bold text-gray-900 mb-5">Novi vodomer</h2>

        <div className="space-y-4">
          {([
            { key: 'subscriber_name' as const, label: 'Ime pretplatnika', required: true },
            { key: 'address' as const,         label: 'Adresa',           required: true },
            { key: 'meter_serial' as const,    label: 'Serijski broj',    required: true },
          ] as const).map(({ key, label, required }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
              </label>
              <input
                type="text"
                value={form[key] ?? ''}
                onChange={set(key)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
              />
            </div>
          ))}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tip vodomera</label>
            <select
              value={form.meter_type ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, meter_type: e.target.value }))}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden bg-white"
            >
              <option value="">— nije odabrano —</option>
              <option value="cold">Hladna voda</option>
            </select>
          </div>

          <GpsFields
            lat={lat} lng={lng}
            geocodeFrom={form.address}
            onLatChange={setLat}
            onLngChange={setLng}
          />
        </div>

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleSubmit}
            disabled={mutation.isPending || !form.subscriber_name || !form.address || !form.meter_serial}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Kreiranje…' : 'Kreirati vodomer'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg"
          >
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MetersPage() {
  const { user } = useAuthStore()
  const canEdit = user?.roles.some((r) => CAN_EDIT_ROLES.includes(r)) ?? false
  const canRead = user?.roles.some((r) => CAN_READ_ROLES.includes(r)) ?? false

  const [rawSearch, setRawSearch]           = useState('')
  const [debouncedSearch, setDebouncedSearch] = useDebounce()
  const [showInactive, setShowInactive]     = useState(false)
  const [selected, setSelected]             = useState<Meter | null>(null)
  const [creating, setCreating]             = useState(false)
  const [page, setPage]                     = useState(0)
  const [viewMode, setViewMode]             = useState<'table' | 'map'>('table')
  const PAGE_SIZE = 50

  // Map mode fetches by viewport (MeterMapView, below) instead of paging --
  // this query now only drives the table, so it's disabled while the map
  // is showing rather than pulling a redundant fixed-size page nobody reads.
  const { data: meters = [], isLoading } = useQuery({
    queryKey: ['meters', debouncedSearch, showInactive, page],
    queryFn: () =>
      readingsApi.getMeters({
        search: debouncedSearch || undefined,
        active: showInactive ? undefined : true,
        limit:  PAGE_SIZE,
        skip:   page * PAGE_SIZE,
      }).then((r) => r.data),
    staleTime: 30_000,
    enabled: viewMode === 'table',
  })

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRawSearch(e.target.value)
    setDebouncedSearch(e.target.value)
    setPage(0)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-80">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Pretraga po imenu, adresi, broju…"
            value={rawSearch}
            onChange={handleSearch}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-hidden"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <div
            onClick={() => { setShowInactive((s) => !s); setPage(0) }}
            className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${showInactive ? 'bg-brand-cyan' : 'bg-gray-300'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${showInactive ? 'translate-x-4' : ''}`} />
          </div>
          Prikaži neaktivne
        </label>

        <div className="flex-1" />

        <span className="text-sm text-gray-500">
          {isLoading ? '…' : `${meters.length} vodomera`}
        </span>

        {/* View toggle */}
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('table')}
            title="Tabela"
            className={`p-1.5 ${viewMode === 'table' ? 'bg-brand-cyan text-brand-midnight' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 6h18M3 14h18M3 18h18" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('map')}
            title="Mapa"
            className={`p-1.5 ${viewMode === 'map' ? 'bg-brand-poi text-brand-midnight' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </button>
        </div>

        {canEdit && (
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novi vodomer
          </button>
        )}
      </div>

      {/* Content: map or table */}
      {viewMode === 'map' ? (
        <div className="flex-1 overflow-hidden">
          <MeterMapView search={debouncedSearch} showInactive={showInactive} />
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
              </div>
            ) : meters.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <svg className="w-10 h-10 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Nema vodomera</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Pretplatnik</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Serijski br.</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Adresa</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Posl. oč. (m³)</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">Datum</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {meters.map((m) => (
                    <tr
                      key={m.id}
                      onClick={() => setSelected(m)}
                      className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-3.5 max-w-[200px]">
                        <p className="font-medium text-gray-900 truncate">{m.subscriber_name}</p>
                        {m.reading_route_name && (
                          <span className="text-xs text-brand-cyan-text font-medium">{m.reading_route_name}</span>
                        )}
                      </td>
                      <td className="px-6 py-3.5 font-mono text-gray-500 text-xs">{m.meter_serial}</td>
                      <td className="px-6 py-3.5 text-gray-600 hidden md:table-cell max-w-[220px] truncate">{m.address}</td>
                      <td className="px-6 py-3.5 text-right font-semibold text-gray-900">
                        {m.last_reading_value !== null ? m.last_reading_value.toFixed(3) : <span className="text-gray-400 font-normal">—</span>}
                      </td>
                      <td className="px-6 py-3.5 text-gray-500 hidden lg:table-cell">{fmtDate(m.last_reading_date)}</td>
                      <td className="px-6 py-3.5 text-center"><StatusBadge active={m.active} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {(meters.length === PAGE_SIZE || page > 0) && (
            <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-3 flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prethodna
              </button>
              <span className="text-sm text-gray-500">Strana {page + 1}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={meters.length < PAGE_SIZE}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Sledeća →
              </button>
            </div>
          )}
        </>
      )}

      {/* Drawer */}
      {selected && (
        <MeterDrawer
          meter={selected}
          onClose={() => setSelected(null)}
          canEdit={canEdit}
          canRead={canRead}
        />
      )}

      {/* Create modal */}
      {creating && <CreateMeterModal onClose={() => setCreating(false)} />}
    </div>
  )
}
